<template>
  <div class="carousel-admin">
    <div class="header">
      <h2>Gestionar Carrousel</h2>
      <button type="button" @click="toggleForm" class="add-btn desktop-add-btn">
        {{ showForm ? 'Cancelar' : '+ Agregar Track' }}
      </button>
    </div>

    <div v-if="showForm" class="form-container">
      <form @submit.prevent="submitForm">
        <div class="form-group">
          <label>Nombre del Track</label>
          <input v-model="formData.name" type="text" required>
        </div>

        <div v-if="editingId" class="form-group">
          <label>Posición en el carrousel</label>
          <select v-model="formPosition" class="form-select" required>
            <option v-for="idx in items.length" :key="'pos-' + idx" :value="String(idx - 1)">
              Posición {{ idx }}
            </option>
          </select>
          <p class="field-hint">Orden en el que aparece el track en el carrousel público.</p>
        </div>
        <div v-else class="form-group">
          <label>Posición en el carrousel</label>
          <select v-model="formPosition" class="form-select">
            <option value="">Último (por defecto)</option>
            <option v-for="idx in items.length + 1" :key="'posn-' + idx" :value="String(idx - 1)">
              Posición {{ idx }}
            </option>
          </select>
          <p class="field-hint">Si no eliges una posición, el track se agrega al final.</p>
        </div>

        <div class="form-group">
          <label>Imagen</label>
          <div class="file-input-wrapper">
            <input
              ref="fileInput"
              type="file"
              @change="handleImageSelect"
              accept="image/*"
              :required="!editingId"
              :disabled="uploadingImage"
            >
            <div v-if="imagePreview" class="image-preview">
              <img :src="imagePreview" alt="Preview">
              <button type="button" @click="clearImage" class="clear-btn">Limpiar imagen</button>
            </div>
          </div>
          <p v-if="uploadingImage" class="uploading-text">Subiendo imagen...</p>
        </div>

        <div class="form-group checkbox">
          <input v-model="formData.has_listen_button" type="checkbox" id="has-listen-btn" class="custom-checkbox">
          <label for="has-listen-btn" class="checkbox-label">Link de escucha</label>
        </div>

        <div v-if="formData.has_listen_button" class="form-group">
          <label>Link (Beatport, Spotify, etc)</label>
          <input v-model="formData.link" type="url">
        </div>

        <button type="submit" class="submit-btn" :disabled="loading || uploadingImage || !formData.image_url">
          {{ loading ? 'Guardando...' : uploadingImage ? 'Subiendo imagen...' : 'Guardar' }}
        </button>
      </form>
    </div>

    <div v-if="!showForm" class="items-grid">
      <div v-if="items.length === 0" class="empty-state">
        No hay tracks aún
      </div>

      <div v-for="item in items" :key="item.id" class="item-card">
        <img :src="item.image_url" :alt="item.name" class="item-image">
        <div class="item-info">
          <h3>{{ item.name }}</h3>
        </div>
        <div class="item-actions">
          <button @click="editItem(item)" class="edit-btn">Editar</button>
          <button @click="confirmDelete(item)" class="delete-btn">Eliminar</button>
        </div>
      </div>
    </div>

    <div class="action-footer mobile-action-footer">
      <button type="button" @click="toggleForm" class="add-btn">
        {{ showForm ? 'Cancelar' : '+ Agregar Track' }}
      </button>
    </div>

    <div v-if="showDeleteModal" class="modal-overlay" @click.self="closeDeleteModal">
      <div class="modal-content">
        <h3>Confirmar eliminación</h3>
        <p>¿Estás seguro de que quieres eliminar <strong>{{ itemToDelete?.name }}</strong>?</p>
        <p class="warning">Esta acción no se puede deshacer.</p>
        <div class="modal-actions">
          <button @click="closeDeleteModal" class="cancel-btn">Cancelar</button>
          <button @click="confirmDeleteAction" class="confirm-delete-btn" :disabled="deleting">
            {{ deleting ? 'Eliminando...' : 'Eliminar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { uploadImage } from '@/services/storageService'
import { fetchWithAuth } from '@/services/apiService'
import { apiUrl } from '@/services/apiConfig'

export default {
  name: 'CarouselAdmin',
  data() {
    return {
      items: [],
      showForm: false,
      loading: false,
      uploadingImage: false,
      imagePreview: null,
      formData: {
        name: '',
        image_url: '',
        link: '',
        has_listen_button: true
      },
      formPosition: '',
      editingId: null,
      showDeleteModal: false,
      itemToDelete: null,
      deleting: false
    }
  },
  mounted() {
    this.fetchItems()
  },
  methods: {
    toggleForm() {
      this.showForm = !this.showForm
      if (this.showForm && !this.editingId) {
        this.formPosition = ''
      }
    },

    buildCarouselPayload() {
      const payload = {
        name: this.formData.name,
        image_url: this.formData.image_url,
        link: this.formData.link,
        has_listen_button: this.formData.has_listen_button
      }
      if (this.editingId) {
        payload.position = parseInt(this.formPosition, 10)
      } else if (this.formPosition !== '' && this.formPosition != null) {
        payload.position = parseInt(this.formPosition, 10)
      }
      return payload
    },

    async fetchItems() {
      try {
        const response = await fetchWithAuth(apiUrl('/api/carousel'))
        this.items = await response.json()
      } catch (error) {
        console.error('Error fetching items:', error)
      }
    },

    async handleImageSelect(event) {
      const file = event.target.files[0]
      if (!file) return

      // Preview local
      const reader = new FileReader()
      reader.onload = (e) => {
        this.imagePreview = e.target.result
      }
      reader.readAsDataURL(file)

      // Subir a Firebase
      this.uploadingImage = true
      try {
        const downloadURL = await uploadImage(file, 'carousel')
        this.formData.image_url = downloadURL
      } catch (error) {
        console.error('Error uploading image:', error)
        alert('Error al subir la imagen')
        this.imagePreview = null
      } finally {
        this.uploadingImage = false
      }
    },

    clearImage() {
      this.imagePreview = null
      this.formData.image_url = ''
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    async submitForm() {
      this.loading = true
      try {
        const url = this.editingId
          ? apiUrl(`/api/carousel/${this.editingId}`)
          : apiUrl('/api/carousel')
        
        const method = this.editingId ? 'PUT' : 'POST'

        const response = await fetchWithAuth(url, {
          method,
          body: JSON.stringify(this.buildCarouselPayload())
        })

        if (response.ok) {
          this.formData = { name: '', image_url: '', link: '', has_listen_button: true }
          this.formPosition = ''
          this.editingId = null
          this.showForm = false
          this.imagePreview = null
          this.fetchItems()
        }
      } catch (error) {
        console.error('Error saving item:', error)
      } finally {
        this.loading = false
      }
    },

    editItem(item) {
      this.formData = {
        ...item,
        has_listen_button: item.has_listen_button !== false
      }
      this.formPosition = String(item.position != null ? item.position : 0)
      this.editingId = item.id
      this.showForm = true
      this.imagePreview = item.image_url
    },

    confirmDelete(item) {
      this.itemToDelete = item
      this.showDeleteModal = true
    },

    closeDeleteModal() {
      this.showDeleteModal = false
      this.itemToDelete = null
    },

    async confirmDeleteAction() {
      if (!this.itemToDelete) return

      this.deleting = true
      try {
        await fetchWithAuth(apiUrl(`/api/carousel/${this.itemToDelete.id}`), {
          method: 'DELETE'
        })
        this.closeDeleteModal()
        this.fetchItems()
      } catch (error) {
        console.error('Error deleting item:', error)
        alert('Error al eliminar el track')
      } finally {
        this.deleting = false
      }
    },

    async deleteItem(id) {
      if (!confirm('¿Estás seguro de que quieres eliminar este track?')) return

      try {
        await fetchWithAuth(apiUrl(`/api/carousel/${id}`), {
          method: 'DELETE'
        })
        this.fetchItems()
      } catch (error) {
        console.error('Error deleting item:', error)
      }
    }
  }
}
</script>

<style scoped>
.carousel-admin {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0f0f0;
}

.header h2 {
  font-size: 1.5rem;
  margin: 0;
  color: #333;
}

.form-container {
  background: #f9f9f9;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid #f0f0f0;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
}

.form-group input:not([type="file"]) {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.form-group input:not([type="file"]):focus {
  outline: none;
  border-color: #333;
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
}

.form-select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
}

.form-select:focus {
  outline: none;
  border-color: #333;
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
}

.field-hint {
  margin: 0.4rem 0 0 0;
  font-size: 0.8rem;
  color: #666;
  font-weight: 400;
}

.file-input-wrapper {
  position: relative;
}

.file-input-wrapper input[type="file"] {
  display: block;
  width: 100%;
  padding: 1rem;
  border: 2px dashed #ddd;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #fafafa;
  font-size: 0.9rem;
}

.file-input-wrapper input[type="file"]:hover:not(:disabled) {
  border-color: #333;
  background: #f5f5f5;
}

.file-input-wrapper input[type="file"]:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.image-preview {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #f9f9f9;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
}

.image-preview img {
  max-width: 150px;
  max-height: 150px;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.clear-btn {
  padding: 0.5rem 1rem;
  background: #ff6b6b;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  transition: all 0.3s ease;
}

.clear-btn:hover {
  background: #ff5252;
}

.uploading-text {
  color: #666;
  font-size: 0.9rem;
  margin: 0.5rem 0 0 0;
  font-style: italic;
}

.form-group.checkbox {
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.8rem;
}

.form-group.checkbox input {
  width: auto;
  margin: 0;
}

.checkbox-label {
  margin-bottom: 0 !important; 
  margin: 0;
  cursor: pointer;
  font-weight: 600;
  color: #333;
}

.custom-checkbox {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  width: 20px;
  height: 20px;
  border: 2px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
  vertical-align: middle;
  position: relative;
  flex-shrink: 0;
  min-width: 20px;
}

.custom-checkbox:hover {
  border-color: #333;
}

.custom-checkbox:checked {
  background: #4caf50;
  border-color: #4caf50;
}

.custom-checkbox:checked::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-weight: bold;
  font-size: 14px;
  line-height: 1;
}

.submit-btn {
  padding: 0.75rem 2rem;
  background: #333;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
}

.submit-btn:hover:not(:disabled) {
  background: #000;
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.items-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
}

.empty-state {
  grid-column: 1 / -1;
  text-align: center;
  color: #999;
  padding: 2rem;
}

.action-footer {
  display: none;
}

.add-btn {
  padding: 0.75rem 1.5rem;
  background: #333;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
}

.add-btn:hover {
  background: #000;
}

.item-card {
  background: white;
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
}

.item-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #ddd;
}

.item-image {
  width: 100%;
  height: 200px;
  object-fit: cover;
  background: #f0f0f0;
}

.item-info {
  padding: 1rem;
  flex: 1;
}

.item-info h3 {
  font-size: 1rem;
  margin: 0 0 0.5rem 0;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-link {
  color: #0066cc;
  text-decoration: none;
  font-size: 0.85rem;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-link:hover {
  text-decoration: underline;
}

.item-actions {
  display: flex;
  gap: 0.5rem;
  padding: 0 1rem 1rem 1rem;
}

.edit-btn,
.delete-btn {
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  transition: all 0.3s ease;
}

.edit-btn {
  background: #f0f0f0;
  color: #333;
}

.edit-btn:hover {
  background: #e0e0e0;
}

.delete-btn {
  background: #ffe0e0;
  color: #d00;
}

.delete-btn:hover {
  background: #ffd0d0;
}

@media (max-width: 768px) {
  .carousel-admin {
    padding: 1rem;
  }

  .header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }

  .items-grid {
    grid-template-columns: 1fr;
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-content {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 400px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-content h3 {
  font-size: 1.25rem;
  margin: 0 0 1rem 0;
  color: #333;
}

.modal-content p {
  color: #666;
  margin: 0.75rem 0;
  line-height: 1.5;
}

.modal-content p.warning {
  color: #d00;
  font-size: 0.9rem;
  margin-top: 1rem;
}

.modal-content strong {
  color: #333;
  font-weight: 600;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
}

.cancel-btn,
.confirm-delete-btn {
  flex: 1;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  font-size: 0.95rem;
}

.cancel-btn {
  background: #f0f0f0;
  color: #333;
}

.cancel-btn:hover {
  background: #e0e0e0;
}

.confirm-delete-btn {
  background: #ff6b6b;
  color: white;
}

.confirm-delete-btn:hover:not(:disabled) {
  background: #ff5252;
}

.confirm-delete-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .carousel-admin {
    padding: 1rem;
  }

  .header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }

  .desktop-add-btn {
    display: none;
  }

  .mobile-action-footer {
    display: flex;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 20;
    padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
    background: white;
    border-top: 1px solid #e6e6e6;
    box-shadow: 0 -4px 14px rgba(0, 0, 0, 0.08);
  }

  .mobile-action-footer .add-btn {
    width: 100%;
  }

  .items-grid {
    grid-template-columns: 1fr;
    padding-bottom: 5rem;
  }
}

@media (max-width: 480px) {
  .carousel-admin {
    padding: 0.75rem;
  }

  .header h2 {
    font-size: 1.2rem;
  }

  .mobile-action-footer .add-btn {
    padding: 0.95rem 1rem;
    font-size: 0.9rem;
  }

  .form-group label {
    font-size: 0.85rem;
  }

  .form-group input:not([type="file"]) {
    padding: 0.6rem;
    font-size: 0.9rem;
  }

  .items-grid {
    gap: 1rem;
  }

  .item-card {
    border-radius: 8px;
  }

  .item-image {
    height: 150px;
  }

  .edit-btn,
  .delete-btn {
    padding: 0.4rem;
    font-size: 0.75rem;
  }
}
</style>
